import { sendAnalyticsData } from '@avalara/bat-apps-shared/helpers/analytics';
import { trackEvent } from '@avalara/bat-apps-shared/lib/analytics';
import type { Connector } from '@avalara/types/index';

import type { ProductInfo } from '@avalara/types/analytics';
import { formatPrice } from '../../../shared/helpers';
import { getRiskAssessmentPricingData } from '../../reducers/aggregate-logic';
import { getOrderTotal, getValidatedConnector } from '../../selectors/order';
import { selectReturnsPricingData } from '../../selectors/returns';
import analytics from '../analytics';
import { RATE_PLAN_IDS } from '../constants';

import type { Dispatch } from '@avalara/types/actions';
import type { TierPlan } from '@avalara/types/index';
import type { State } from '@avalara/types/reducer-states';

type AvataxPurchaseProps = {
  selectedTier: TierPlan | null;
  connector: Connector;
  price: number;
};

export const trackAvataxPurchase = ({ selectedTier, connector, price }: AvataxPurchaseProps) => {
  if (selectedTier == null) return;
  const productInfo1 = {
    productId: RATE_PLAN_IDS.avataxServiceProAnnual,
    productName: 'Avalara Avatax',
  };
  type PlatformKey = keyof typeof RATE_PLAN_IDS.avataxAnnualPlatform;
  const productInfo2 = {
    productId: connector.level != null ? RATE_PLAN_IDS.avataxAnnualPlatform[connector.level as PlatformKey] : '',
    productName: connector.name,
  };
  const product = [
    {
      productInfo: productInfo1,
    },
    {
      productInfo: productInfo2,
    },
  ];
  const transaction = {
    item: [
      {
        productInfo: productInfo1,
        quantity: selectedTier.quantity.toString(),
      },
      {
        productInfo: productInfo2,
        quantity: selectedTier.tierNumber.toString(),
      },
    ],
    total: {
      transactionTotal: price,
    },
  };

  trackEvent({ product, transaction, eventName: 'Purchase', eventType: 'Transaction' });
};

const getProductInfoByFlow = (state: State): ProductInfo | null => {
  const {
    system: { activeFlow },
    esb: { esbPartner, esbConfigMap },
  } = state;

  const partnerName =
    esbConfigMap != null && esbPartner != null && esbConfigMap[esbPartner] ? esbConfigMap[esbPartner].name : '';

  const returnsPricing = selectReturnsPricingData(state);

  const { salestax: riskAssessmentSalesTax, totalPrice: riskAssessmentTotalPrice } =
    getRiskAssessmentPricingData(state);

  const purchaseType = 'NEW PURCHASE';

  switch (activeFlow) {
    case 'returns':
      return {
        category: 'Managed Returns',
        productId: 'Buydot | Returns | MR',
        quantity: '1',
        totalPrice: returnsPricing.total.toString(),
        salesTax: returnsPricing.salestax.toString(),
        purchaseType,
      };
    case 'risk-assessment':
    case 'risk-assessment-net-new':
      return {
        category: 'Risk Assessment',
        productId: 'Buydot | Risk Assessment',
        totalPrice: riskAssessmentTotalPrice.toString(),
        salesTax: riskAssessmentSalesTax.toString(),
        purchaseType,
      };
    case 'esb-trial':
      return {
        category: 'ESB',
        productId: `Buydot | ESB Trial | ${partnerName}`,
        totalPrice: '0',
        salesTax: '0',
        purchaseType,
      };
    default:
      return null;
  }
};

const eventTypeDict: { [eventTrack: string]: string | null | undefined } = {
  account_start: 'Account Form Start',
  purchase_start: 'Purchase Form Start',
  buy_now: 'Buy now',
  purchase: 'purchase',
};

const getOfferProductInfo = (state: State): ProductInfo => {
  const {
    offer: { analyticsProductCategory },
    order: { quantity, salesTax, selectedPlanName },
  } = state;

  const connector = getValidatedConnector(state);

  const productIdEnding = selectedPlanName ? ` | ${selectedPlanName}` : '';
  const purchaseType = 'NEW PURCHASE';

  return {
    category: analyticsProductCategory,
    productId: `Buydot | ${analyticsProductCategory}${productIdEnding}`,
    quantity,
    totalPrice: formatPrice(getOrderTotal(state)),
    salesTax: formatPrice(salesTax ?? 0),
    connectorId: connector?.id || '',
    connectorName: connector?.name || '',
    purchaseType,
  };
};

export const updateProductAnalytics =
  (eventTrack: string, useOfferFlow?: boolean) => async (_: Dispatch, getState: () => State) => {
    if (!window.digitalData) return;

    const eventType = eventTypeDict[eventTrack];
    const productInfo = useOfferFlow ? getOfferProductInfo(getState()) : getProductInfoByFlow(getState());

    if (!productInfo || !eventType) return;

    window.digitalData.event = { eventInfo: { eventType } };
    window.digitalData.product = [{ productInfo }];

    if (eventTrack !== 'purchase') sendAnalyticsData(eventTrack);
  };

export const trackOfferPurchaseAnalytics =
  (offerFlow: string, accountId: number | string, isNewAccount: boolean) =>
  async (dispatch: Dispatch, getState: () => State) => {
    const state = getState();
    const {
      abTesting: { group },
      order: { quantity, selectedPlanName },
    } = state;

    const prefix = isNewAccount ? 'NET_NEW' : 'ADD';
    analytics.track(`${prefix}_${offerFlow.toUpperCase()}_SUCCESS`, {
      abBucket: group,
      accountId,
      offerId: offerFlow,
      plan: selectedPlanName,
      price: getOrderTotal(state),
      quantity,
      eventType: 'PURCHASE_SUCCESS',
      flow: offerFlow,
      purchaseType: prefix,
    });

    dispatch(updateProductAnalytics('purchase', true));
  };
